import type {
  GetApplicantHandoffViewResult,
  GetInviteeHandoffViewResult,
} from '@findigs/api';
import { formatUnitAddress } from '@findigs/helpers/src/helpers/format/unit-address';
import { usePostHogClient } from '@findigs/helpers/src/integrations/posthog/use-posthog-client';
import { Button } from '@findigs/ui-kit/src/components/button';
import { ProgressView } from '@findigs/ui-kit/src/components/progress-view';
import { RemoteImage } from '@findigs/ui-kit/src/components/remote-image';
import { Link as RemixLink } from '@remix-run/react';
import { useEffect, useMemo, useState } from 'react';
import { styles } from './handoff-landing.module.scss';
import PinIcon from '@findigs/ui-kit/src/icons/material/place.filled.inline.svg';
import PoweredByFindigs from '@findigs/ui-kit/src/images/logo/powered-by-findigs.inline.svg';

export type HandoffLandingProps = GetApplicantHandoffViewResult &
  GetInviteeHandoffViewResult & {
    invite_id?: string;
    app_group_id?: string;
    co_applicant_type?: 'applicant' | 'guarantor';
    description?: string;
    searchParams?: URLSearchParams;
  };

type TestControlHeadlineProps = {
  co_applicant_type?: 'applicant' | 'guarantor';
  is_generic_unit: boolean;
};
const TestControlHeadline = ({
  co_applicant_type,
  is_generic_unit,
}: TestControlHeadlineProps) => {
  return (
    <>
      {is_generic_unit || co_applicant_type === 'guarantor' ? (
        <>
          You’re heading to <br className="md:d-none" />
          Findigs to apply
        </>
      ) : (
        <>
          Found the place? <br className="md:d-none" />
          Let’s make it yours.
        </>
      )}
    </>
  );
};
type TestControlCopyProps = {
  description?: string;
  pm_name: string;
};
const TestControlCopy = ({ description, pm_name }: TestControlCopyProps) => {
  return (
    <>
      {description
        ? description
        : `${pm_name} uses Findigs as their secure application partner. We’ll help you send a complete app so you can get your decision and get moving.`}
    </>
  );
};

/**
 * Loads the experiment flag from PostHog (client side)
 *
 * @remarks
 * - SSR-safe: will be `undefined` until the client is ready
 * - If the flag cannot be loaded within 6 seconds, it will default to `'control'`
 */
function useExperimentFlag() {
  const [featureFlag, setFeatureFlag] = useState<string | boolean | undefined>(
    undefined
  );

  const postHogClient = usePostHogClient();
  useEffect(() => {
    const timeout = setTimeout(() => {
      setFeatureFlag((v) => v ?? 'control');
    }, 6000);
    /* istanbul ignore next */
    if (!postHogClient) {
      return () => {
        clearTimeout(timeout);
      };
    }

    const key = 'handoff-screen-test';
    const value = postHogClient.getFeatureFlag(key);
    if (value == null) {
      postHogClient.onFeatureFlags(() => {
        const value = postHogClient.getFeatureFlag(key);
        setFeatureFlag((v) => v ?? value);
      });
    } else {
      setFeatureFlag((v) => v ?? value);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [postHogClient]);

  return featureFlag;
}

export const HandoffLanding = ({
  address_determinants,
  invite_id,
  unit_app_id,
  app_group_id,
  pm_name,
  pm_logo_url,
  co_applicant_type,
  description,
  searchParams,
}: HandoffLandingProps) => {
  const redirectTo = useMemo(() => {
    const params = new URLSearchParams();
    for (const [paramKey, paramValue] of Object.entries({
      unitAppId: unit_app_id,
      appGroupId: app_group_id,
      inviteId: invite_id,
      isGuarantor:
        co_applicant_type == null
          ? undefined
          : co_applicant_type === 'guarantor',
    })) {
      if (paramValue) {
        params.append(paramKey, paramValue.toString());
      }
    }

    //add all existing search params for welcome to process
    if (searchParams) {
      for (const [paramKey, paramValue] of searchParams.entries()) {
        params.append(paramKey, paramValue);
      }
    }

    return `/welcome/?${params.toString()}`;
  }, [searchParams, unit_app_id, app_group_id, invite_id, co_applicant_type]);

  const signupLink = useMemo(() => {
    return `/signup?redirectTo=${encodeURIComponent(redirectTo)}`;
  }, [redirectTo]);

  const loginLink = useMemo(() => {
    return `/login?redirectTo=${encodeURIComponent(redirectTo)}`;
  }, [redirectTo]);

  const testGroup1Headline = `Get approved in minutes — No credit impact`;
  const testGroup1Copy = `Your application is enabled by Findigs, ${pm_name}’s secure application partner. Everyday we process hundreds of applications - helping renters get decisions quickly and securely.`;
  const testGroup1CTA = 'Get started';
  const experimentFlag = useExperimentFlag();

  return experimentFlag == null ? (
    <ProgressView loading={true} />
  ) : (
    <main className={styles}>
      <div className="header">
        {pm_logo_url ? (
          <RemoteImage
            disableLoadAnimation
            className="pmc"
            height={48}
            src={pm_logo_url}
            alt={pm_name}
          />
        ) : (
          <span className="text-t-sm md:text-t-lg">{pm_name}</span>
        )}
        <PoweredByFindigs
          className="findigs text-tintsNeutral90"
          role="img"
          aria-label="Powered by Findigs"
          width="124"
          height="21"
        />
      </div>
      <div className="content">
        {!address_determinants.is_generic_unit && (
          <div className="flex-center text-b-md-bold mb-6 sm:text-b-lg-bold sm:mb-10">
            <PinIcon aria-hidden={true} className="pin mr-2 sm:mr-4" />
            <span>{formatUnitAddress(address_determinants)}</span>
          </div>
        )}
        <div className="text-t-sm mb-2 sm:text-t-lg sm:mb-6">
          {experimentFlag == 'test_group_1' ? (
            <h1>{testGroup1Headline}</h1>
          ) : (
            <TestControlHeadline
              co_applicant_type={co_applicant_type}
              is_generic_unit={address_determinants.is_generic_unit}
            />
          )}
        </div>
        <div className="description text-tintsNeutral90 mb-12 sm:text-b-lg-regular">
          {experimentFlag == 'test_group_1' ? (
            <p>{testGroup1Copy}</p>
          ) : (
            <TestControlCopy {...{ description, pm_name }} />
          )}
        </div>

        <div className="buttons">
          <Button variant="FilledBrand" as={RemixLink} to={signupLink}>
            {experimentFlag == 'test_group_1'
              ? testGroup1CTA
              : 'Create account'}
          </Button>
          <Button variant="Text" as={RemixLink} to={loginLink}>
            Log in
          </Button>
        </div>
      </div>
    </main>
  );
};
